import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Layout, { Container } from "../components/layout";
import { apiurl } from "../config/config";
import { SuwasthiLoader } from "../components/loader";
import Pagination from "../components/pagination";
import { FaSearch } from "react-icons/fa";
import { toast } from "react-toastify";
import { useUser } from "../config/userProvider";
import { X } from "lucide-react";

const Referrals = () => {
  const { userData } = useUser();
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedRef, setSelectedRef] = useState(null);

  useEffect(() => {
    fetchReferences();
  }, [currentPage, itemsPerPage]);

  const fetchReferences = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${apiurl}/api/employee/reference/get?page=${currentPage}&limit=${itemsPerPage}`
      );
      const data = await res.json();
      if (data.success) {
        setReferences(data.references || []);
        setTotalItems(data.pagination?.totalItems || 0);
      }
    } catch (err) {
      console.error("Error fetching references:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredRefs = references.filter((ref) =>
    ref.candidateName?.toLowerCase().includes(search.toLowerCase())
  );

  const listVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: { delay: index * 0.05 },
    }),
  };

  return (
    <Layout>
      <Container overflow="overflow-scroll">
        <div className="p-4 bg-gray-50 min-h-screen">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
            <h2 className="text-2xl font-semibold text-gray-800">
              Employee Referrals
            </h2>

            <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border w-full md:w-1/3 shadow-sm">
              <FaSearch className="text-gray-400" />
              <input
                type="text"
                placeholder="Search candidate name..."
                className="w-full focus:outline-none text-gray-700"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Table or Loader */}
          {loading ? (
            <SuwasthiLoader />
          ) : (
            <div className="bg-white shadow-md rounded-xl overflow-x-auto p-4">
              {filteredRefs.length === 0 ? (
                <p className="text-gray-500 text-center py-10">
                  No references found.
                </p>
              ) : (
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-100 text-gray-700 text-left">
                      <th className="p-3">Reference ID</th>
                      <th className="p-3">Candidate</th>
                      <th className="p-3">Phone</th>
                      <th className="p-3">Department</th>
                      <th className="p-3">Position</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Referral Date</th>
                      {/* {userData.role === "manager" && ( */}
                        <th className="p-3 text-center">Action</th>
                      {/* )} */}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRefs.map((ref, index) => (
                      <motion.tr
                        key={ref._id}
                        className="border-b hover:bg-gray-50"
                        variants={listVariants}
                        initial="hidden"
                        animate="visible"
                        custom={index}
                      >
                        <td className="p-3 font-medium text-gray-800">
                          {ref.referenceId}
                        </td>
                        <td className="p-3">{ref.candidateName}</td>
                        <td className="p-3">{ref.candidatePhone}</td>
                        <td className="p-3">{ref.department || "-"}</td>
                        <td className="p-3">{ref.position || "-"}</td>
                        <td className="p-3">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                              ref.status === "joined"
                                ? "bg-green-100 text-green-700"
                                : ref.status === "selected"
                                ? "bg-blue-100 text-blue-700"
                                : ref.status === "rejected"
                                ? "bg-red-100 text-red-700"
                                : "bg-yellow-100 text-yellow-700"
                            }`}
                          >
                            {ref.status}
                          </span>
                        </td>
                        <td className="p-3 text-gray-600 text-sm">
                          {new Date(ref.referralDate).toLocaleDateString()}
                        </td>

                        {/* {userData.role === "manager" && ( */}
                          <td className="p-3 text-center">
                            <button
                              onClick={() => setSelectedRef(ref)}
                              className="text-indigo-600 font-medium hover:underline"
                            >
                              Manage
                            </button>
                          </td>
                        {/* )} */}
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Items per page + Pagination */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mt-5 gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Items per page:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="border rounded-md px-2 py-1 text-sm"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>

            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      </Container>

      {selectedRef && (
        <ManageReferenceModal
          reference={selectedRef}
          onClose={() => setSelectedRef(null)}
          onSuccess={fetchReferences}
          userData={userData}
        />
      )}
    </Layout>
  );
};

export default Referrals;

const ManageReferenceModal = ({ reference, onClose, onSuccess, userData }) => {
  const [formData, setFormData] = useState({
    status: reference.status || "pending",
    remarks: reference.remarks || "",
    joinedDate: reference.joinedDate
      ? reference.joinedDate.split("T")[0]
      : "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${apiurl}/api/employee/reference/update`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          referenceObjId: reference._id,
          status: formData.status,
          remarks: formData.remarks,
          processBy:{
            userName : userData.firstName + " " + userData.lastName || "-",
            employeeCode : `SUW${userData?.employeeDetails.employeeId || "-"}`,
          },
          joinedDate: formData.joinedDate,
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Reference updated successfully!");
        onSuccess();
        onClose();
      } else {
        toast.error(data.message || "Update failed");
      }
    } catch (error) {
      console.error("Error updating reference:", error);
      toast.error("Error updating reference");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50 px-3">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white w-full max-w-xl rounded-2xl p-6 shadow-xl relative"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-600 hover:text-black"
        >
          <X size={22} />
        </button>

        {/* Title */}
        <h2 className="text-xl font-semibold mb-4 text-gray-800">
          Manage Reference —{" "}
          <span className="text-indigo-600 font-bold">{reference.referenceId}</span>
        </h2>

        {/* Candidate Info */}
        <div className="bg-gray-50 p-3 rounded-lg border mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700">
            <p>
              <span className="font-medium text-gray-800">Candidate Name:</span>{" "}
              {reference.candidateName}
            </p>
            <p>
              <span className="font-medium text-gray-800">Phone:</span>{" "}
              {reference.candidatePhone}
            </p>
            <p>
              <span className="font-medium text-gray-800">Email:</span>{" "}
              {reference.candidateEmail || "-"}
            </p>
            <p>
              <span className="font-medium text-gray-800">Department:</span>{" "}
              {reference.department || "-"}
            </p>
            <p>
              <span className="font-medium text-gray-800">Position:</span>{" "}
              {reference.position || "-"}
            </p>
            <p>
              <span className="font-medium text-gray-800">Location:</span>{" "}
              {reference.location || "-"}
            </p>
            <p>
              <span className="font-medium text-gray-800">Referral Date:</span>{" "}
              {new Date(reference.referralDate).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Referred By Info */}
        <div className="bg-indigo-50 p-3 rounded-lg border mb-4">
          <h3 className="font-semibold text-indigo-700 mb-2 text-sm">
            Referred By
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700">
            <p>
              <span className="font-medium text-gray-800">Name:</span>{" "}
              {reference.referredBy?.employeeName || "-"}
            </p>
            <p>
              <span className="font-medium text-gray-800">Employee Code:</span>{" "}
              {reference.referredBy?.employeeCode || "-"}
            </p>
            <p>
              <span className="font-medium text-gray-800">Employee ID:</span>{" "}
              {reference.referredBy?.employeeId || "-"}
            </p>
          </div>
        </div>

        {/* Processed By Info (if exists) */}
        {reference.processBy && (
          <div className="bg-green-50 p-3 rounded-lg border mb-4">
            <h3 className="font-semibold text-green-700 mb-2 text-sm">
              Processed By
            </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700">
            <p>
              <span className="font-medium text-gray-800">Name:</span>{" "}
              {reference.processBy?.userName || "-"}
            </p>
            <p>
              <span className="font-medium text-gray-800">Employee Code:</span>{" "}
              {reference.processBy?.employeeCode || "-"}
            </p>
          </div>
          </div>
        )}

        {/* Status, Joined Date, Remarks */}
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-2">
          <div className="flex flex-col">
            <label className="text-sm text-gray-600 mb-1 font-medium">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="border p-2 rounded-lg focus:ring focus:ring-indigo-200"
            >
              <option value="shortlisted">Shortlisted</option>
              <option value="pending">Pending</option>
              <option value="selected">Selected</option>
              <option value="joined">Joined</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
{/* 
          <div className="flex flex-col">
            <label className="text-sm text-gray-600 mb-1 font-medium">
              Joined Date
            </label>
            <input
              type="date"
              name="joinedDate"
              value={formData.joinedDate}
              onChange={handleChange}
              className="border p-2 rounded-lg focus:ring focus:ring-indigo-200"
            />
          </div> */}
        </div>

        <div className="flex flex-col mt-4">
          <label className="text-sm text-gray-600 mb-1 font-medium">
            Remarks
          </label>
          <textarea
            name="remarks"
            placeholder="Add remarks here..."
            value={formData.remarks}
            onChange={handleChange}
            className="border w-full p-2 rounded-lg focus:ring focus:ring-indigo-200 min-h-[80px]"
          />
        </div>

        {/* Footer Buttons */}
        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            disabled={loading}
            className="bg-indigo-600 text-white px-5 py-2 rounded-lg hover:bg-indigo-700 transition"
          >
            {loading ? "Updating..." : "Save Changes"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};